import type { MilkdownPlugin } from '@milkdown/kit/ctx';
import { parserCtx, prosePluginsCtx, schemaCtx, SchemaReady } from '@milkdown/kit/core';
import { isTextOnlySlice } from '@milkdown/kit/prose';
import { DOMParser, DOMSerializer } from '@milkdown/kit/prose/model';
import { Plugin, PluginKey } from '@milkdown/kit/prose/state';

import { normalizeOrphanTaskListPlainText, plainTextHasOrphanTaskLines } from './taskListPasteNormalize';
import type { ClipboardMarkdownParser } from './tableCellImage';

const vscodeMarkdownPasteKey = new PluginKey('brainfishVscodeMarkdownPaste');

/** Collect every string that might be a VS Code / Cursor language id from clipboard JSON (shape varies by version). */
function vscodeClipboardLanguageHints(data: unknown): string[] {
  if (!data || typeof data !== 'object') {
    return [];
  }
  const o = data as Record<string, unknown>;
  const out: string[] = [];
  const push = (v: unknown) => {
    if (typeof v === 'string' && v.trim()) {
      out.push(v.trim());
    }
  };
  push(o.mode);
  push(o.languageId);
  push(o.language);

  const nested = o.editor;
  if (nested && typeof nested === 'object') {
    const e = nested as Record<string, unknown>;
    push(e.mode);
    push(e.languageId);
  }

  return out;
}

/**
 * True when clipboard `vscode-editor-data` identifies a Markdown family language.
 * Exported for tests. VS Code / Cursor may send `languageId`, `mode`, or scopes like `text.html.markdown`.
 */
export function isVscodeClipboardMarkdownSource(vscodeDataRaw: string): boolean {
  let parsed: unknown;
  try {
    parsed = JSON.parse(vscodeDataRaw) as unknown;
  } catch {
    return false;
  }

  for (const language of vscodeClipboardLanguageHints(parsed)) {
    if (languageLooksLikeMarkdownSource(language)) {
      return true;
    }
  }

  return false;
}

function languageLooksLikeMarkdownSource(language: string): boolean {
  const id = language.toLowerCase();
  if (id === 'markdown' || id === 'md' || id === 'mdx' || id === 'mdc' || id === 'qmd' || id === 'markdoc') {
    return true;
  }

  // Hyphen/suffix markdown variants from grammars / extensions (e.g. markdown-math, vscode Markdown mode ids)
  if (/^markdown([._-]|$)/i.test(language)) {
    return true;
  }

  return /\b(?:quarto|rmd|rmarkdown|mdx|mdc|qmd|markdoc)\b/i.test(language) || /\bmarkdown\b/i.test(language);
}

/**
 * Milkdown's clipboard plugin pastes any VS Code / Cursor selection with `vscode-editor-data` as a **code block**
 * (`data.mode` becomes the fence language). For Markdown sources that inserts a `markdown` code fence instead of
 * parsing the copied text. This plugin runs first and parses `text/plain` as Markdown when the VS Code mode is md.
 */
export const vscodeMarkdownPastePlugin: MilkdownPlugin = (ctx) => {
  let prosePlugin: Plugin | undefined;

  return async () => {
    await ctx.wait(SchemaReady);

    prosePlugin = new Plugin({
      key: vscodeMarkdownPasteKey,
      props: {
        handlePaste: (view, event) => {
          const parser = ctx.get(parserCtx);
          const schema = ctx.get(schemaCtx);
          const editable = view.props.editable?.(view.state);
          const { clipboardData } = event;
          if (!editable || !clipboardData) {
            return false;
          }

          const vscodeDataRaw = clipboardData.getData('vscode-editor-data');
          if (!vscodeDataRaw || !isVscodeClipboardMarkdownSource(vscodeDataRaw)) {
            return false;
          }

          const currentNode = view.state.selection.$from.node();
          if (currentNode.type.spec.code) {
            return false;
          }

          let text = clipboardData.getData('text/plain');
          if (text.length === 0) {
            return false;
          }

          if (plainTextHasOrphanTaskLines(text)) {
            const normalized = normalizeOrphanTaskListPlainText(text);
            if (normalized !== text) {
              text = normalized;
            }
          }

          const plain = text.replace(/\r\n?/g, '\n');

          let mdSlice: ReturnType<ClipboardMarkdownParser> | undefined;
          try {
            mdSlice = parser(plain);
          } catch {
            mdSlice = undefined;
          }

          // Avoid falling through to Milkdown's vscode → code_fence path when remark/parser rejects input.
          if (!mdSlice || typeof mdSlice === 'string') {
            event.preventDefault();
            view.dispatch(view.state.tr.insertText(plain));

            return true;
          }

          event.preventDefault();

          const dom = DOMSerializer.fromSchema(schema).serializeFragment(mdSlice.content);
          const domParser = DOMParser.fromSchema(schema);
          const slice = domParser.parseSlice(dom);
          const node = isTextOnlySlice(slice);
          if (node) {
            view.dispatch(view.state.tr.replaceSelectionWith(node, true));

            return true;
          }

          try {
            view.dispatch(view.state.tr.replaceSelection(slice));

            return true;
          } catch {
            return false;
          }
        },
      },
    });

    ctx.update(prosePluginsCtx, (ps) => [prosePlugin!, ...ps]);

    return () => {
      ctx.update(prosePluginsCtx, (ps) => ps.filter((p) => p !== prosePlugin));
    };
  };
};
